// Dashboard API service for analytics and statistics
import { API_CONFIG, API_ENDPOINTS } from '../constants/config';

// Dashboard response types based on backend documentation
export interface DashboardStats {
  totalBookingsYear: number;
  totalBookingsMonth: number;
  totalAccepted: number;
  totalPending: number;
  totalCancelled: number;
  totalRejected: number;
  totalCompleted: number;
  averageGuestPerReservation: number;
  occupancyRate: number;
}

export interface TrendBreakdown {
  accepted: number;
  pending: number;
  cancelled: number;
  rejected: number;
}

export interface TrendMetrics {
  acceptanceRate: number;
  cancellationRate: number;
  rejectionRate: number;
}

export interface MonthlyTrend {
  month: string;
  bookings: number;
  breakdown: TrendBreakdown;
  metrics: TrendMetrics;
}

export interface DailyTrend {
  day: string;
  date: string; // YYYY-MM-DD format
  bookings: number;
  breakdown: TrendBreakdown;
  metrics: TrendMetrics;
}

export interface MonthlyTrendsResponse {
  status: string;
  message: string;
  data: {
    year: number;
    trends: MonthlyTrend[];
  };
}

export interface DailyTrendsResponse {
  status: string;
  message: string;
  data: {
    month: string;
    year: number;
    trends: DailyTrend[];
  };
}

class DashboardApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (import.meta.env.VITE_API_URL || 'https://grandpittu-backend.onrender.com');
    const url = `${baseUrl}${API_CONFIG.SLUG}${endpoint}`;

    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...options,
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `Dashboard API Error: ${response.status} - ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch {
            // Keep original message
          }
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error: unknown) {

      // Retry logic for network/timeout errors
      const isRetryable = error instanceof Error && (
        error.name === 'TimeoutError' ||
        error.name === 'AbortError' ||
        error.message?.includes('fetch') ||
        error.message?.includes('network')
      );

      if (isRetryable && retryCount < API_CONFIG.RETRY_ATTEMPTS) {
        const delay = API_CONFIG.EXPONENTIAL_BACKOFF
          ? API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount)
          : API_CONFIG.RETRY_DELAY;

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Get dashboard statistics
   * GET /dashboard/stats
   */
  async getDashboardStats(year?: number): Promise<DashboardStats> {
    const endpoint = year
      ? `${API_ENDPOINTS.DASHBOARD.STATS}?year=${year}`
      : API_ENDPOINTS.DASHBOARD.STATS;
    return this.makeRequest<DashboardStats>(endpoint);
  }

  /**
   * Get statistics by specific month
   * GET /dashboard/stats/month
   */
  async getStatsByMonth(year: number, month: number): Promise<DashboardStats> {
    const endpoint = `${API_ENDPOINTS.DASHBOARD.STATS_BY_MONTH}?year=${year}&month=${month}`;
    return this.makeRequest<DashboardStats>(endpoint);
  }

  /**
   * Get current month statistics
   * GET /dashboard/stats/current-month
   */
  async getCurrentMonthStats(): Promise<DashboardStats> {
    return this.makeRequest<DashboardStats>(API_ENDPOINTS.DASHBOARD.STATS_CURRENT_MONTH);
  }

  /**
   * Get monthly trends
   * GET /dashboard/trends/monthly
   */
  async getMonthlyTrends(year?: number): Promise<MonthlyTrendsResponse> {
    const endpoint = year
      ? `${API_ENDPOINTS.DASHBOARD.TRENDS_MONTHLY}?year=${year}`
      : API_ENDPOINTS.DASHBOARD.TRENDS_MONTHLY;
    return this.makeRequest<MonthlyTrendsResponse>(endpoint);
  }

  /**
   * Get daily trends for current month
   * GET /dashboard/trends/daily
   */
  async getDailyTrends(): Promise<DailyTrendsResponse> {
    return this.makeRequest<DailyTrendsResponse>(API_ENDPOINTS.DASHBOARD.TRENDS_DAILY);
  }

  /**
   * Get daily trends by specific month
   * GET /dashboard/trends/daily/month
   */
  async getDailyTrendsByMonth(year: number, month: number): Promise<DailyTrendsResponse> {
    const endpoint = `${API_ENDPOINTS.DASHBOARD.TRENDS_DAILY_BY_MONTH}?year=${year}&month=${month}`;
    return this.makeRequest<DailyTrendsResponse>(endpoint);
  }
}

// Export singleton instance
export const dashboardApi = new DashboardApiService();
export default dashboardApi;